import React from 'react';
import { Link } from 'react-router-dom';
import { Database } from 'lucide-react';
import Card from '../../components/Card';
import { useAuth } from '../../contexts/AuthContext';
import {
  ADMIN_GROUPS,
  adminDestinationsFor,
  type AdminDestination,
} from '../../config/adminNav';

/**
 * SettingsPage - the admin hub.
 *
 * Every card is generated from `config/adminNav`, filtered to what this viewer can actually
 * open. That filter is the point: the hub is reachable by both admin tiers, and a workspace
 * Owner offered a link to Email Configuration would only be told "Access Denied" on arrival.
 * Because the table also states the tier each route is guarded by, a card and its route
 * cannot disagree.
 */
const SettingsPage: React.FC = () => {
    const { isTenantAdmin, isPlatformAdmin } = useAuth();

    const visible = adminDestinationsFor({ isTenantAdmin, isPlatformAdmin });
    const inGroup = (group: string): AdminDestination[] =>
        visible.filter((d) => d.group === group);

    // Platform administrators run the installation; a client administers only their workspace.
    const title = isPlatformAdmin ? 'System Settings' : 'Workspace Settings';
    const subtitle = isPlatformAdmin
        ? 'Configure application-wide settings and administer client workspaces'
        : 'Manage your workspace’s team, access and preferences';

    return (
        <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">{title}</h1>
                <p className="text-gray-600 dark:text-gray-400">{subtitle}</p>
            </div>

            <div className="grid gap-6">
                {ADMIN_GROUPS.map((group) => {
                    const destinations = inGroup(group.id);
                    if (destinations.length === 0) return null;

                    return (
                        <Card key={group.id} variant="elevated">
                            <Card.Header title={group.title} subtitle={group.subtitle} />
                            <Card.Body>
                                <div className="space-y-4">
                                    {destinations.map(({ path, label, description, icon: Icon }) => (
                                        <Link key={path} to={path} className="block">
                                            <div className="flex items-start gap-3 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors cursor-pointer">
                                                <Icon className="w-5 h-5 text-primary-600 mt-0.5" />
                                                <div className="flex-1">
                                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">{label}</h3>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    );
                })}

                {/* Not a link — informational, and only Akhile operates the installation. */}
                {isPlatformAdmin && (
                    <Card variant="elevated">
                        <Card.Header
                            title="Database & Maintenance"
                            subtitle="System maintenance and backup configuration"
                        />
                        <Card.Body>
                            <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                <Database className="w-5 h-5 text-gray-900 dark:text-gray-100 mt-0.5" />
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">Backup & Recovery</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Automated backup schedules, retention policies, and recovery options.</p>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default SettingsPage;
